import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  CircularProgress,
  Avatar,
  Switch,
  FormControlLabel,
  InputAdornment,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField as MuiTextField,
} from '@mui/material';
import {
  Person as PersonIcon,
  Security as SecurityIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Share as ShareIcon,
  LockReset as LockResetIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Shield as ShieldIcon,
  ShieldOutlined as ShieldOutlinedIcon,
  CalendarToday as CalendarIcon,
  Help as HelpIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Groups as GroupsIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/config';
import * as echarts from 'echarts';
import { CHECKLIST_DATA } from '../data/checklistData';
import SecEventCalendar from './SecEventCalendar';
import EventSuggestionsManagement from '../components/EventSuggestionsManagement';

// Tab panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Custom ECharts component
const EChartsComponent = ({ option, style, id }) => {
  const chartRef = React.useRef(null);
  const chartInstance = React.useRef(null);

  React.useEffect(() => {
    if (chartRef.current) {
      // Initialize chart
      chartInstance.current = echarts.init(chartRef.current);
      
      // Set option
      chartInstance.current.setOption(option);
      
      // Handle resize
      const handleResize = () => {
        if (chartInstance.current) {
          chartInstance.current.resize();
        }
      };
      
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('resize', handleResize);
        if (chartInstance.current) {
          chartInstance.current.dispose();
        }
      };
    }
  }, [option]);

  React.useEffect(() => {
    if (chartInstance.current && option) {
      chartInstance.current.setOption(option, true);
    }
  }, [option]);

  return (
    <div
      ref={chartRef}
      id={id}
      style={{ width: '100%', height: '100%', ...style }}
    />
  );
};

// ImagePreview component for authenticated image loading
const ImagePreview = ({ attachmentId, filename }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadImage = async () => {
      try {
        setLoading(true);
        setError(false);
        
        // Fetch the image with authentication
        const response = await axios.get(`/api/v1/reports/attachments/${attachmentId}`, {
          responseType: 'blob',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        // Create object URL from blob
        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const url = URL.createObjectURL(blob);
        setImageUrl(url);
        
      } catch (err) {
        console.error('Failed to load image:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadImage();

    // Cleanup object URL on unmount
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [attachmentId]);

  const handleImageClick = () => {
    if (imageUrl) {
      window.open(imageUrl, '_blank');
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '200px',
        backgroundColor: '#f5f5f5',
        borderRadius: '4px'
      }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        p: 2, 
        textAlign: 'center',
        color: 'text.secondary',
        backgroundColor: '#f5f5f5',
        borderRadius: '4px'
      }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Image could not be loaded
        </Typography>
        <Button 
          size="small" 
          onClick={() => window.open(`/api/v1/reports/attachments/${attachmentId}`, '_blank')}
        >
          Open in new tab
        </Button>
      </Box>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={filename}
      style={{
        maxWidth: '100%',
        maxHeight: '400px',
        objectFit: 'contain',
        borderRadius: '4px',
        cursor: 'pointer'
      }}
      onClick={handleImageClick}
    />
  );
};

const AdminDashboard = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);

  // Helper function to get video title from checklist data
  const getVideoTitle = (moduleName, itemId, videoType) => {
    try {
      const basissicherheitCategory = CHECKLIST_DATA.find(cat => cat.category === 'Basissicherheit');
      if (!basissicherheitCategory) return null;
      
      const item = basissicherheitCategory.items.find(i => 
        i.id === itemId && i.moduleName === moduleName
      );
      
      if (!item) return null;
      
      // Return item title
      return item.title || null;
    } catch (error) {
      console.error('Error getting video title:', error);
      return null;
    }
  };

  // Helper function to get milieu name from code
  const getMilieuName = (code) => {
    const mapping = {
      'ap': 'Adaptiv‑Pragmatische Mitte',
      'kg': 'Konservativ‑Gehoben',
      'kh': 'Konsum‑Hedonistisch',
      'öko': 'Neo‑Ökologisch',
      'pre': 'Prekär',
      'tra': 'Traditionell'
    };
    return mapping[code] || code;
  };
  const [users, setUsers] = useState([]);
  const [userSortBy, setUserSortBy] = useState('created_at');
  const [userSortOrder, setUserSortOrder] = useState('desc');
  const [systemStats, setSystemStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    mfaEnabledUsers: 0,
    adminUsers: 0,
    emailInvitations: 0,
    shareActivities: 0,
    pendingReports: 0,
    pendingEventSuggestions: 0,
    pendingQuestions: 0,
    quizCompletions: 0,
  });
  const [quizStats, setQuizStats] = useState({
    total_completions: 0,
    completion_rate: 0,
    milieu_distribution: {},
    cluster_distribution: {},
    average_score: 0,
    completions_by_date: [],
  });
  const [videoStats, setVideoStats] = useState({
    total_views: 0,
    unique_viewers: 0,
    average_rating: null,
    total_feedback: 0,
    videos: [],
  });
  const [registrationTrends, setRegistrationTrends] = useState({
    daily: [],
    weekly: [],
    monthly: []
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editUserData, setEditUserData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    is_superuser: false,
    is_ambassador: false
  });
  const [passwordResetDialogOpen, setPasswordResetDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [updatingUsers, setUpdatingUsers] = useState(new Set());

  // Reports Management State
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsStats, setReportsStats] = useState(null);
  const [reportsPage, setReportsPage] = useState(0);
  const [reportsRowsPerPage] = useState(10);
  const [reportsTotal, setReportsTotal] = useState(0);
  const [reportsFilter, setReportsFilter] = useState({
    status: '',
    report_type: '',
    priority: ''
  });
  const [viewReportDialogOpen, setViewReportDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [updateStatusDialogOpen, setUpdateStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  
  // Questions Management State
  const [questions, setQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionsPage, setQuestionsPage] = useState(0);
  const [questionsRowsPerPage] = useState(10);
  const [questionsTotal, setQuestionsTotal] = useState(0);
  const [questionsFilter, setQuestionsFilter] = useState({
    status: ''
  });
  const [viewQuestionDialogOpen, setViewQuestionDialogOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [answerQuestionDialogOpen, setAnswerQuestionDialogOpen] = useState(false);
  const [questionAnswer, setQuestionAnswer] = useState('');
  const [questionCategory, setQuestionCategory] = useState('');
  const [dismissReasonDialogOpen, setDismissReasonDialogOpen] = useState(false);
  const [dismissReason, setDismissReason] = useState('');
  const [questionToDismiss, setQuestionToDismiss] = useState(null);
  const [availableCategories, setAvailableCategories] = useState([
    'Phishing',
    'Passwörter',
    'Malware',
    'Sicherheit',
    'Datenschutz',
    'Betrug',
    'Sonstiges'
  ]);
  
  // MFA-related states
  const [mfaDisableDialogOpen, setMfaDisableDialogOpen] = useState(false);
  
  // Force logout states
  const [forceLogoutDialogOpen, setForceLogoutDialogOpen] = useState(false);
  
  // Audit logs states
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);
  const [auditLogsTotal, setAuditLogsTotal] = useState(0);
  const [auditLogsPage, setAuditLogsPage] = useState(0);
  const [auditLogsRowsPerPage, setAuditLogsRowsPerPage] = useState(25);
  const [auditLogsFilters, setAuditLogsFilters] = useState({
    user_id: '',
    action: '',
    resource: ''
  });

  // Fetch users data
  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
        setSystemStats(data.stats || systemStats);
      } else {
        showSnackbar('Failed to fetch users data', 'error');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      showSnackbar('Error fetching users data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch registration trends data
  const fetchRegistrationTrends = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin/registration-trends`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setRegistrationTrends(data);
      } else {
        console.error('Failed to fetch registration trends');
      }
    } catch (error) {
      console.error('Error fetching registration trends:', error);
    }
  };

  // Fetch quiz statistics
  const fetchQuizStatistics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin/quiz-statistics`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setQuizStats(data);
      } else {
        console.error('Failed to fetch quiz statistics');
      }
    } catch (error) {
      console.error('Error fetching quiz statistics:', error);
    }
  };

  // Fetch video statistics
  const fetchVideoStatistics = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin/video-statistics`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        setVideoStats(data);
      } else {
        console.error('Failed to fetch video statistics:', response.status, response.statusText);
        // Set default empty stats to prevent loading state
        setVideoStats({
          total_views: 0,
          unique_viewers: 0,
          average_rating: null,
          total_feedback: 0,
          videos: [],
        });
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('Video statistics request timed out');
      } else {
        console.error('Error fetching video statistics:', error);
      }
      // Set default empty stats to prevent loading state
      setVideoStats({
        total_views: 0,
        unique_viewers: 0,
        average_rating: null,
        total_feedback: 0,
        videos: [],
      });
    }
  };

  // Fetch pending reports count
  const fetchPendingReportsCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin/reports/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const pendingCount = data.statistics?.status_counts?.pending || 0;
        setSystemStats(prev => ({ ...prev, pendingReports: pendingCount }));
      } else {
        console.error('Failed to fetch pending reports count');
      }
    } catch (error) {
      console.error('Error fetching pending reports count:', error);
    }
  };

  // Fetch pending event suggestions count
  const fetchPendingEventSuggestionsCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_CONFIG.BASE_URL}/event-suggestions?status=pending`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const pendingCount = Array.isArray(data) ? data.length : 0;
        setSystemStats(prev => ({ ...prev, pendingEventSuggestions: pendingCount }));
      } else {
        console.error('Failed to fetch pending event suggestions count');
      }
    } catch (error) {
      console.error('Error fetching pending event suggestions count:', error);
    }
  };

  // Fetch pending questions count
  const fetchPendingQuestionsCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin/questions?status=pending&limit=1`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const pendingCount = data.total_count || 0;
        setSystemStats(prev => ({ ...prev, pendingQuestions: pendingCount }));
      } else {
        console.error('Failed to fetch pending questions count');
      }
    } catch (error) {
      console.error('Error fetching pending questions count:', error);
    }
  };

  // Fetch audit logs data
  const fetchAuditLogs = async () => {
    setAuditLogsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        limit: auditLogsRowsPerPage.toString(),
        offset: (auditLogsPage * auditLogsRowsPerPage).toString(),
      });

      // Add filters if they exist
      if (auditLogsFilters.user_id) params.append('user_id', auditLogsFilters.user_id);
      if (auditLogsFilters.action) params.append('action', auditLogsFilters.action);
      if (auditLogsFilters.resource) params.append('resource', auditLogsFilters.resource);

      const response = await fetch(`${API_CONFIG.BASE_URL}/admin/audit-logs?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data.audit_logs || []);
        setAuditLogsTotal(data.total_count || 0);
      } else {
        showSnackbar('Failed to fetch audit logs', 'error');
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      showSnackbar('Error fetching audit logs', 'error');
    } finally {
      setAuditLogsLoading(false);
    }
  };

  // Update user status
  const updateUserStatus = async (userId, field, value) => {
    // Prevent multiple simultaneous updates for the same user
    if (updatingUsers.has(userId)) {
      console.log('Update already in progress for user:', userId);
      return;
    }

    setUpdatingUsers(prev => new Set(prev).add(userId));

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin/users/${userId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ [field]: value }),
      });

      if (response.ok) {
        const data = await response.json();
        // Check if user was deactivated and provide specific feedback
        if (field === 'is_active' && value === false) {
          showSnackbar('User deactivated and force logged out successfully', 'success');
        } else if (field === 'is_active' && value === true) {
          showSnackbar('User reactivated - will need to login again', 'success');
        } else {
          showSnackbar('User updated successfully', 'success');
        }
        fetchUsers(); // Refresh data
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Update user error:', errorData);
        showSnackbar(`Failed to update user: ${errorData.detail || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      showSnackbar('Error updating user', 'error');
    } finally {
      setUpdatingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  // Update multiple user fields at once
  const updateUserMultiple = async (userId, updates) => {
    // Prevent multiple simultaneous updates for the same user
    if (updatingUsers.has(userId)) {
      console.log('Update already in progress for user:', userId);
      return false;
    }

    setUpdatingUsers(prev => new Set(prev).add(userId));

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin/users/${userId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      if (response.ok) {
        // Check if user was deactivated and provide specific feedback
        if (updates.is_active === false) {
          showSnackbar('User deactivated and force logged out successfully', 'success');
        } else if (updates.is_active === true) {
          showSnackbar('User reactivated - will need to login again', 'success');
        } else {
          showSnackbar('User updated successfully', 'success');
        }
        fetchUsers(); // Refresh data
        return true;
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Update user error:', errorData);
        showSnackbar(`Failed to update user: ${errorData.detail || 'Unknown error'}`, 'error');
        return false;
      }
    } catch (error) {
      console.error('Error updating user:', error);
      showSnackbar('Error updating user', 'error');
      return false;
    } finally {
      setUpdatingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  // Show snackbar notification
  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle user edit
  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditUserData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      is_superuser: user.is_superuser || false,
      is_ambassador: user.is_ambassador || false
    });
    setEditDialogOpen(true);
  };

  // Handle password reset
  const handlePasswordReset = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setPasswordResetDialogOpen(true);
  };

  // Reset user password
  const resetUserPassword = async () => {
    if (!selectedUser || !newPassword) {
      showSnackbar('Please enter a new password', 'error');
      return;
    }

    if (newPassword.length < 12) {
      showSnackbar('Password must be at least 12 characters long', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin/users/${selectedUser.id}/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ new_password: newPassword }),
      });

      if (response.ok) {
        const data = await response.json();
        showSnackbar(data.message, 'success');
        setPasswordResetDialogOpen(false);
        setNewPassword('');
        fetchUsers(); // Refresh data
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Password reset error:', errorData);
        showSnackbar(`Failed to reset password: ${errorData.detail || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      showSnackbar('Error resetting password', 'error');
    }
  };

  // MFA Disable Function
  const handleMfaDisable = (user) => {
    setSelectedUser(user);
    setMfaDisableDialogOpen(true);
  };

  // Force Logout Functions
  const handleForceLogout = (user) => {
    setSelectedUser(user);
    setForceLogoutDialogOpen(true);
  };

  const forceLogoutUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin/users/${selectedUser.id}/force-logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        showSnackbar(data.message, 'success');
        setForceLogoutDialogOpen(false);
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        showSnackbar(`Failed to force logout: ${errorData.detail || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Error forcing logout:', error);
      showSnackbar('Error forcing logout', 'error');
    }
  };


  const disableMfa = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin/users/${selectedUser.id}/mfa/disable-admin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        showSnackbar(data.message, 'success');
        setMfaDisableDialogOpen(false);
        fetchUsers(); // Refresh data
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        showSnackbar(`Failed to disable MFA: ${errorData.detail || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Error disabling MFA:', error);
      showSnackbar('Error disabling MFA', 'error');
    }
  };


  // Handle user status toggle
  const handleToggleUserStatus = (userId, currentStatus, field) => {
    // Prevent admin from deactivating themselves
    if (userId === currentUser?.id && field === 'is_active' && currentStatus === true) {
      showSnackbar('Cannot deactivate your own account', 'error');
      return;
    }
    
    // Prevent admin from removing their own admin privileges
    if (userId === currentUser?.id && field === 'is_superuser' && currentStatus === true) {
      showSnackbar('Cannot remove your own admin privileges', 'error');
      return;
    }
    
    // Prevent self-assignment of ambassador role
    if (userId === currentUser?.id && field === 'is_ambassador') {
      showSnackbar('Cannot assign or remove ambassador role for yourself', 'error');
      return;
    }
    
    updateUserStatus(userId, field, !currentStatus);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    return status ? 'success' : 'error';
  };

  // Get status icon
  const getStatusIcon = (status) => {
    return status ? <CheckCircleIcon /> : <CancelIcon />;
  };

  // Create chart options for registration trends
  const createChartOptions = (data, title, xAxisLabel) => {
    return {
      title: {
        text: title,
        left: 'center',
        textStyle: {
          fontSize: 16,
          fontWeight: 'normal'
        }
      },
      tooltip: {
        trigger: 'axis',
        formatter: function (params) {
          const date = new Date(params[0].axisValue).toLocaleDateString('de-DE');
          return `${date}<br/>Registrierungen: ${params[0].value}`;
        }
      },
      xAxis: {
        type: 'category',
        data: data.map(item => item.date),
        name: xAxisLabel,
        nameLocation: 'middle',
        nameGap: 30,
        axisLabel: {
          formatter: function (value) {
            return new Date(value).toLocaleDateString('de-DE', { 
              month: 'short', 
              day: 'numeric' 
            });
          }
        }
      },
      yAxis: {
        type: 'value',
        name: 'Anzahl Registrierungen',
        nameLocation: 'middle',
        nameGap: 50
      },
      series: [{
        data: data.map(item => item.count),
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: {
          color: '#1976d2',
          width: 3
        },
        itemStyle: {
          color: '#1976d2'
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [{
              offset: 0, color: 'rgba(25, 118, 210, 0.3)'
            }, {
              offset: 1, color: 'rgba(25, 118, 210, 0.05)'
            }]
          }
        }
      }],
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '15%',
        containLabel: true
      }
    };
  };

  // Reports Management Functions
  const fetchReports = async () => {
    setReportsLoading(true);
    try {
      const params = new URLSearchParams({
        limit: reportsRowsPerPage.toString(),
        offset: (reportsPage * reportsRowsPerPage).toString()
      });
      
      if (reportsFilter.status) params.append('status', reportsFilter.status);
      if (reportsFilter.report_type) params.append('report_type', reportsFilter.report_type);
      if (reportsFilter.priority) params.append('priority', reportsFilter.priority);
      
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin/reports?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReports(data.reports);
        setReportsTotal(data.total_count);
      } else {
        throw new Error('Failed to fetch reports');
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch reports',
        severity: 'error'
      });
    } finally {
      setReportsLoading(false);
    }
  };

  const fetchReportsStats = async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin/reports/stats`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReportsStats(data.statistics);
      }
    } catch (error) {
      console.error('Error fetching reports stats:', error);
    }
  };

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setViewReportDialogOpen(true);
  };

  const handleUpdateReportStatus = (report) => {
    setSelectedReport(report);
    setNewStatus(report.status);
    setStatusNotes('');
    setUpdateStatusDialogOpen(true);
  };

  const updateReportStatus = async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin/reports/${selectedReport.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          status: newStatus,
          notes: statusNotes
        })
      });
      
      if (response.ok) {
        setSnackbar({
          open: true,
          message: 'Report status updated successfully',
          severity: 'success'
        });
        setUpdateStatusDialogOpen(false);
        fetchReports();
        fetchReportsStats();
      } else {
        throw new Error('Failed to update report status');
      }
    } catch (error) {
      console.error('Error updating report status:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update report status',
        severity: 'error'
      });
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRegistrationTrends();
    fetchQuizStatistics();
    fetchVideoStatistics();
    fetchPendingReportsCount();
    fetchPendingEventSuggestionsCount();
    fetchPendingQuestionsCount();
  }, []);

  // Fetch reports when the reports tab is selected
  useEffect(() => {
    if (tabValue === 3) {
      fetchReports();
      fetchReportsStats();
    }
  }, [tabValue, reportsPage, reportsFilter]);

  // Fetch audit logs when the audit logs tab is selected
  useEffect(() => {
    if (tabValue === 2) {
      fetchAuditLogs();
    }
  }, [tabValue]);

  // Fetch all unique categories from questions
  const fetchAllCategories = async () => {
    try {
      // Fetch a large number of questions to get all categories
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin/questions?limit=1000`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const categories = new Set([
          'Phishing',
          'Passwörter',
          'Malware',
          'Sicherheit',
          'Datenschutz',
          'Betrug',
          'Sonstiges'
        ]);
        
        // Extract unique categories from all questions
        if (data.questions && Array.isArray(data.questions)) {
          data.questions.forEach(q => {
            if (q.kategorie && q.kategorie.trim()) {
              categories.add(q.kategorie.trim());
            }
          });
        }
        
        setAvailableCategories(Array.from(categories).sort());
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Keep default categories if fetch fails
    }
  };

  // Questions Management Functions
  const fetchQuestions = async () => {
    setQuestionsLoading(true);
    try {
      const params = new URLSearchParams({
        limit: questionsRowsPerPage.toString(),
        offset: (questionsPage * questionsRowsPerPage).toString()
      });
      
      if (questionsFilter.status) params.append('status', questionsFilter.status);
      
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin/questions?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setQuestions(data.questions);
        setQuestionsTotal(data.total_count);
      } else {
        throw new Error('Failed to fetch questions');
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch questions',
        severity: 'error'
      });
    } finally {
      setQuestionsLoading(false);
    }
  };

  const handleViewQuestion = (question) => {
    setSelectedQuestion(question);
    setViewQuestionDialogOpen(true);
  };

  const handleAnswerQuestion = (question) => {
    setSelectedQuestion(question);
    setQuestionAnswer('');
    setQuestionCategory(question.kategorie || '');
    setAnswerQuestionDialogOpen(true);
  };

  const submitQuestionAnswer = async () => {
    if (!questionAnswer.trim()) {
      setSnackbar({
        open: true,
        message: 'Please provide an answer',
        severity: 'error'
      });
      return;
    }

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin/questions/${selectedQuestion.id}/answer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          answer: questionAnswer.trim(),
          kategorie: questionCategory.trim() || null
        })
      });

        if (response.ok) {
        setSnackbar({
          open: true,
          message: 'Question answered successfully',
          severity: 'success'
        });
        setAnswerQuestionDialogOpen(false);
        setQuestionAnswer('');
        setQuestionCategory('');
        fetchQuestions();
        fetchPendingQuestionsCount();
        fetchAllCategories(); // Update categories after answering
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to answer question');
      }
    } catch (error) {
      console.error('Error answering question:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to answer question',
        severity: 'error'
      });
    }
  };

  const updateQuestionStatus = async (questionId, status, dismissReason = null) => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin/questions/${questionId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          status: status,
          dismiss_reason: dismissReason
        })
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: 'Question status updated successfully',
          severity: 'success'
        });
        fetchQuestions();
        fetchPendingQuestionsCount();
        fetchAllCategories(); // Update categories after status update
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update question status');
      }
    } catch (error) {
      console.error('Error updating question status:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update question status',
        severity: 'error'
      });
    }
  };

  // Fetch questions when the questions tab is selected
  useEffect(() => {
    if (tabValue === 4) {
      fetchQuestions();
      fetchPendingQuestionsCount();
      fetchAllCategories(); // Fetch all categories when questions tab is opened
    }
  }, [tabValue, questionsPage, questionsFilter]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            Back to Dashboard
          </Button>
        </Box>
        <Typography variant="h3" component="h1" gutterBottom>
          Admin Dashboard
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Welcome back, {currentUser?.first_name} {currentUser?.last_name}
        </Typography>
      </Box>

      {/* System Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PersonIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Total Users
                  </Typography>
                  <Typography variant="h4">
                    {systemStats.totalUsers}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <CheckCircleIcon color="success" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Active Users
                  </Typography>
                  <Typography variant="h4">
                    {systemStats.activeUsers}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <SecurityIcon color="info" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    MFA Enabled
                  </Typography>
                  <Typography variant="h4">
                    {systemStats.mfaEnabledUsers}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <SettingsIcon color="warning" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Admin Users
                  </Typography>
                  <Typography variant="h4">
                    {systemStats.adminUsers}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <EmailIcon color="secondary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Email Invitations
                  </Typography>
                  <Typography variant="h4">
                    {systemStats.emailInvitations}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <ShareIcon color="info" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Share Activities
                  </Typography>
                  <Typography variant="h4">
                    {systemStats.shareActivities}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <SecurityIcon color="warning" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Pending Reports
                  </Typography>
                  <Typography variant="h4">
                    {systemStats.pendingReports}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <CalendarIcon color="secondary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Pending Event Suggestions
                  </Typography>
                  <Typography variant="h4">
                    {systemStats.pendingEventSuggestions}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <HelpIcon color="warning" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Pending Questions
                  </Typography>
                  <Typography variant="h4">
                    {systemStats.pendingQuestions}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <QuestionAnswerIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Quiz Completions
                  </Typography>
                  <Typography variant="h4">
                    {systemStats.quizCompletions || quizStats.total_completions || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <AnalyticsIcon color="success" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Quiz Completion Rate
                  </Typography>
                  <Typography variant="h4">
                    {quizStats.completion_rate ? `${quizStats.completion_rate}%` : '0%'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

        {/* Tabs */}
        <Paper sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              aria-label="admin tabs"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab label="User Management" />
              <Tab label="System Analytics" />
              <Tab label="Audit Logs" />
              <Tab label="Reports Management" />
              <Tab label="Questions Management" />
              <Tab label="Calendar Management" />
              <Tab label="Event Suggestions" />
              <Tab label="Video Analytics" />
            </Tabs>
          </Box>

        {/* User Management Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5">User Management</Typography>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={() => {
                fetchUsers();
                fetchPendingReportsCount();
                fetchPendingEventSuggestionsCount();
                fetchPendingQuestionsCount();
              }}
            >
              Refresh
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={userSortBy === 'name'}
                      direction={userSortBy === 'name' ? userSortOrder : 'asc'}
                      onClick={() => {
                        if (userSortBy === 'name') {
                          setUserSortOrder(userSortOrder === 'asc' ? 'desc' : 'asc');
                        } else {
                          setUserSortBy('name');
                          setUserSortOrder('asc');
                        }
                      }}
                    >
                      User
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={userSortBy === 'email'}
                      direction={userSortBy === 'email' ? userSortOrder : 'asc'}
                      onClick={() => {
                        if (userSortBy === 'email') {
                          setUserSortOrder(userSortOrder === 'asc' ? 'desc' : 'asc');
                        } else {
                          setUserSortBy('email');
                          setUserSortOrder('asc');
                        }
                      }}
                    >
                      Email
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={userSortBy === 'status'}
                      direction={userSortBy === 'status' ? userSortOrder : 'asc'}
                      onClick={() => {
                        if (userSortBy === 'status') {
                          setUserSortOrder(userSortOrder === 'asc' ? 'desc' : 'asc');
                        } else {
                          setUserSortBy('status');
                          setUserSortOrder('asc');
                        }
                      }}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={userSortBy === 'mfa'}
                      direction={userSortBy === 'mfa' ? userSortOrder : 'asc'}
                      onClick={() => {
                        if (userSortBy === 'mfa') {
                          setUserSortOrder(userSortOrder === 'asc' ? 'desc' : 'asc');
                        } else {
                          setUserSortBy('mfa');
                          setUserSortOrder('asc');
                        }
                      }}
                    >
                      MFA
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={userSortBy === 'admin'}
                      direction={userSortBy === 'admin' ? userSortOrder : 'asc'}
                      onClick={() => {
                        if (userSortBy === 'admin') {
                          setUserSortOrder(userSortOrder === 'asc' ? 'desc' : 'asc');
                        } else {
                          setUserSortBy('admin');
                          setUserSortOrder('asc');
                        }
                      }}
                    >
                      Admin
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={userSortBy === 'ambassador'}
                      direction={userSortBy === 'ambassador' ? userSortOrder : 'asc'}
                      onClick={() => {
                        if (userSortBy === 'ambassador') {
                          setUserSortOrder(userSortOrder === 'asc' ? 'desc' : 'asc');
                        } else {
                          setUserSortBy('ambassador');
                          setUserSortOrder('asc');
                        }
                      }}
                    >
                      Ambassador
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={userSortBy === 'created_at'}
                      direction={userSortBy === 'created_at' ? userSortOrder : 'asc'}
                      onClick={() => {
                        if (userSortBy === 'created_at') {
                          setUserSortOrder(userSortOrder === 'asc' ? 'desc' : 'asc');
                        } else {
                          setUserSortBy('created_at');
                          setUserSortOrder('desc');
                        }
                      }}
                    >
                      Created
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={userSortBy === 'last_login_at'}
                      direction={userSortBy === 'last_login_at' ? userSortOrder : 'asc'}
                      onClick={() => {
                        if (userSortBy === 'last_login_at') {
                          setUserSortOrder(userSortOrder === 'asc' ? 'desc' : 'asc');
                        } else {
                          setUserSortBy('last_login_at');
                          setUserSortOrder('desc');
                        }
                      }}
                    >
                      Last Logged In
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  // Sort users based on current sort settings
                  const sortedUsers = [...users].sort((a, b) => {
                    let aValue, bValue;
                    
                    switch (userSortBy) {
                      case 'name':
                        aValue = `${a.first_name || ''} ${a.last_name || ''}`.trim().toLowerCase();
                        bValue = `${b.first_name || ''} ${b.last_name || ''}`.trim().toLowerCase();
                        break;
                      case 'email':
                        aValue = (a.email || '').toLowerCase();
                        bValue = (b.email || '').toLowerCase();
                        break;
                      case 'status':
                        aValue = a.is_active ? 1 : 0;
                        bValue = b.is_active ? 1 : 0;
                        break;
                      case 'mfa':
                        aValue = a.mfa_enabled ? 1 : 0;
                        bValue = b.mfa_enabled ? 1 : 0;
                        break;
                      case 'admin':
                        aValue = a.is_superuser ? 1 : 0;
                        bValue = b.is_superuser ? 1 : 0;
                        break;
                      case 'ambassador':
                        aValue = a.is_ambassador ? 1 : 0;
                        bValue = b.is_ambassador ? 1 : 0;
                        break;
                      case 'created_at':
                        aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
                        bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
                        break;
                      case 'last_login_at':
                        aValue = a.last_login_at ? new Date(a.last_login_at).getTime() : 0;
                        bValue = b.last_login_at ? new Date(b.last_login_at).getTime() : 0;
                        break;
                      default:
                        return 0;
                    }
                    
                    if (aValue < bValue) {
                      return userSortOrder === 'asc' ? -1 : 1;
                    }
                    if (aValue > bValue) {
                      return userSortOrder === 'asc' ? 1 : -1;
                    }
                    return 0;
                  });
                  
                  return sortedUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Avatar sx={{ mr: 2, bgcolor: user.id === currentUser?.id ? 'warning.main' : 'primary.main' }}>
                            {user.first_name?.[0]}{user.last_name?.[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2">
                              {user.first_name} {user.last_name}
                              {user.id === currentUser?.id && (
                                <Chip 
                                  label="You" 
                                  size="small" 
                                  color="warning" 
                                  sx={{ ml: 1, fontSize: '0.7rem' }}
                                />
                              )}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(user.is_active)}
                          label={user.is_active ? 'Active' : 'Inactive'}
                          color={getStatusColor(user.is_active)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(user.mfa_enabled)}
                          label={user.mfa_enabled ? 'Enabled' : 'Disabled'}
                          color={getStatusColor(user.mfa_enabled)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(user.is_superuser)}
                          label={user.is_superuser ? 'Admin' : 'User'}
                          color={user.is_superuser ? 'warning' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={user.is_ambassador || false}
                              onChange={() => handleToggleUserStatus(user.id, user.is_ambassador, 'is_ambassador')}
                              size="small"
                              disabled={updatingUsers.has(user.id) || user.id === currentUser?.id}
                            />
                          }
                          label=""
                        />
                      </TableCell>
                      <TableCell>{formatDate(user.created_at)}</TableCell>
                      <TableCell>{formatDate(user.last_login_at)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Tooltip title="Edit User">
                            <IconButton
                              size="small"
                              onClick={() => handleEditUser(user)}
                              color="primary"
                              disabled={updatingUsers.has(user.id)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reset Password">
                            <IconButton
                              size="small"
                              onClick={() => handlePasswordReset(user)}
                              color="warning"
                              disabled={updatingUsers.has(user.id)}
                            >
                              <LockResetIcon />
                            </IconButton>
                          </Tooltip>
                          {user.mfa_enabled && (
                            <Tooltip title="Disable MFA">
                              <IconButton
                                size="small"
                                onClick={() => handleMfaDisable(user)}
                                color="error"
                                disabled={updatingUsers.has(user.id)}
                              >
                                <ShieldOutlinedIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Force Logout">
                            <IconButton
                              size="small"
                              onClick={() => handleForceLogout(user)}
                              color="warning"
                              disabled={updatingUsers.has(user.id)}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={user.is_active ? "Deactivate User" : "Activate User"}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={user.is_active}
                                  onChange={() => handleToggleUserStatus(user.id, user.is_active, 'is_active')}
                                  size="small"
                                  disabled={updatingUsers.has(user.id) || user.id === currentUser?.id}
                                />
                              }
                              label=""
                            />
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ));
                })()}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* System Analytics Tab */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h5" gutterBottom>
            System Analytics
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    User Registration Trends
                  </Typography>
                  {registrationTrends.daily.length > 0 ? (
                    <EChartsComponent
                      option={createChartOptions(
                        registrationTrends.daily,
                        'Tägliche Registrierungen (letzte 30 Tage)',
                        'Datum'
                      )}
                      style={{ height: '400px', width: '100%' }}
                      id="daily-registrations-chart"
                    />
                  ) : (
                    <Typography color="text.secondary">
                      Keine Registrierungsdaten verfügbar.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Wöchentliche Trends
                  </Typography>
                  {registrationTrends.weekly.length > 0 ? (
                    <EChartsComponent
                      option={createChartOptions(
                        registrationTrends.weekly,
                        'Wöchentliche Registrierungen (letzte 12 Wochen)',
                        'Woche'
                      )}
                      style={{ height: '300px', width: '100%' }}
                      id="weekly-registrations-chart"
                    />
                  ) : (
                    <Typography color="text.secondary">
                      Keine wöchentlichen Daten verfügbar.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Monatliche Trends
                  </Typography>
                  {registrationTrends.monthly.length > 0 ? (
                    <EChartsComponent
                      option={createChartOptions(
                        registrationTrends.monthly,
                        'Monatliche Registrierungen (letzte 12 Monate)',
                        'Monat'
                      )}
                      style={{ height: '300px', width: '100%' }}
                      id="monthly-registrations-chart"
                    />
                  ) : (
                    <Typography color="text.secondary">
                      Keine monatlichen Daten verfügbar.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            
            {/* Quiz Statistics Section */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Quiz Completion Trends
                  </Typography>
                  {quizStats.completions_by_date && quizStats.completions_by_date.length > 0 ? (
                    <EChartsComponent
                      option={createChartOptions(
                        quizStats.completions_by_date,
                        'Quiz-Abschlüsse (letzte 30 Tage)',
                        'Datum'
                      )}
                      style={{ height: '400px', width: '100%' }}
                      id="quiz-completions-chart"
                    />
                  ) : (
                    <Typography color="text.secondary">
                      Keine Quiz-Daten verfügbar.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Milieu Distribution
                  </Typography>
                  {quizStats.milieu_distribution && Object.keys(quizStats.milieu_distribution).length > 0 ? (
                    <EChartsComponent
                      option={{
                        tooltip: {
                          trigger: 'item',
                          formatter: '{b}: {c} ({d}%)'
                        },
                        legend: {
                          orient: 'vertical',
                          left: 'left',
                          top: 'middle'
                        },
                        series: [{
                          type: 'pie',
                          radius: ['40%', '70%'],
                          avoidLabelOverlap: false,
                          itemStyle: {
                            borderRadius: 10,
                            borderColor: '#fff',
                            borderWidth: 2
                          },
                          label: {
                            show: false,
                            position: 'center'
                          },
                          emphasis: {
                            label: {
                              show: true,
                              fontSize: '18',
                              fontWeight: 'bold'
                            }
                          },
                          labelLine: {
                            show: false
                          },
                          data: Object.entries(quizStats.milieu_distribution).map(([code, count]) => ({
                            value: count,
                            name: code
                          }))
                        }]
                      }}
                      style={{ height: '400px', width: '100%' }}
                      id="milieu-distribution-chart"
                    />
                  ) : (
                    <Typography color="text.secondary">
                      Keine Milieu-Daten verfügbar.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Cluster Distribution
                  </Typography>
                  {quizStats.cluster_distribution && Object.keys(quizStats.cluster_distribution).length > 0 ? (
                    <EChartsComponent
                      option={{
                        tooltip: {
                          trigger: 'item',
                          formatter: '{b}: {c} ({d}%)'
                        },
                        legend: {
                          orient: 'vertical',
                          left: 'left',
                          top: 'middle'
                        },
                        series: [{
                          type: 'pie',
                          radius: ['40%', '70%'],
                          avoidLabelOverlap: false,
                          itemStyle: {
                            borderRadius: 10,
                            borderColor: '#fff',
                            borderWidth: 2
                          },
                          label: {
                            show: false,
                            position: 'center'
                          },
                          emphasis: {
                            label: {
                              show: true,
                              fontSize: '18',
                              fontWeight: 'bold'
                            }
                          },
                          labelLine: {
                            show: false
                          },
                          data: Object.entries(quizStats.cluster_distribution).map(([name, count]) => ({
                            value: count,
                            name: name
                          }))
                        }]
                      }}
                      style={{ height: '400px', width: '100%' }}
                      id="cluster-distribution-chart"
                    />
                  ) : (
                    <Typography color="text.secondary">
                      Keine Cluster-Daten verfügbar.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Audit Logs Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5">Audit Logs</Typography>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={fetchAuditLogs}
              disabled={auditLogsLoading}
            >
              Refresh
            </Button>
          </Box>

          {/* Filters */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Filters</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="User ID"
                  value={auditLogsFilters.user_id}
                  onChange={(e) => setAuditLogsFilters({
                    ...auditLogsFilters,
                    user_id: e.target.value
                  })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Action"
                  value={auditLogsFilters.action}
                  onChange={(e) => setAuditLogsFilters({
                    ...auditLogsFilters,
                    action: e.target.value
                  })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Resource"
                  value={auditLogsFilters.resource}
                  onChange={(e) => setAuditLogsFilters({
                    ...auditLogsFilters,
                    resource: e.target.value
                  })}
                  size="small"
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                onClick={() => {
                  setAuditLogsPage(0);
                  fetchAuditLogs();
                }}
                sx={{ mr: 1 }}
              >
                Apply Filters
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setAuditLogsFilters({ user_id: '', action: '', resource: '' });
                  setAuditLogsPage(0);
                  fetchAuditLogs();
                }}
              >
                Clear Filters
              </Button>
            </Box>
          </Paper>

          {/* Audit Logs Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>User ID</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Resource</TableCell>
                  <TableCell>Details</TableCell>
                  <TableCell>IP Address</TableCell>
                  <TableCell>User Agent</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditLogsLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : auditLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No audit logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  auditLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>{formatDate(log.created_at)}</TableCell>
                      <TableCell>{log.user_id || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          label={log.action}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {log.resource && (
                          <Chip
                            label={log.resource}
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={log.details || ''}>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {log.details || 'N/A'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{log.ip_address || 'N/A'}</TableCell>
                      <TableCell>
                        <Tooltip title={log.user_agent || ''}>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 150,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {log.user_agent ? log.user_agent.substring(0, 30) + '...' : 'N/A'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {auditLogsPage * auditLogsRowsPerPage + 1} to{' '}
              {Math.min((auditLogsPage + 1) * auditLogsRowsPerPage, auditLogsTotal)} of{' '}
              {auditLogsTotal} entries
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setAuditLogsPage(Math.max(0, auditLogsPage - 1));
                fetchAuditLogs();
              }}
              disabled={auditLogsPage === 0 || auditLogsLoading}
              sx={{ mr: 1 }}
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setAuditLogsPage(auditLogsPage + 1);
                fetchAuditLogs();
              }}
              disabled={(auditLogsPage + 1) * auditLogsRowsPerPage >= auditLogsTotal || auditLogsLoading}
            >
              Next
            </Button>
          </Box>
        </TabPanel>

        {/* Reports Management Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5">Reports Management</Typography>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={() => {
                fetchReports();
                fetchReportsStats();
                fetchPendingReportsCount();
              }}
            >
              Refresh
            </Button>
          </Box>

          {/* Reports Statistics Cards */}
          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <SecurityIcon color="primary" sx={{ mr: 2 }} />
                    <Box>
                      <Typography color="textSecondary" gutterBottom>
                        Total Reports
                      </Typography>
                      <Typography variant="h4">
                        {reportsStats?.total_reports || 0}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <CheckCircleIcon color="success" sx={{ mr: 2 }} />
                    <Box>
                      <Typography color="textSecondary" gutterBottom>
                        Pending
                      </Typography>
                      <Typography variant="h4">
                        {reportsStats?.status_counts?.pending || 0}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <AnalyticsIcon color="warning" sx={{ mr: 2 }} />
                    <Box>
                      <Typography color="textSecondary" gutterBottom>
                        High Priority
                      </Typography>
                      <Typography variant="h4">
                        {reportsStats?.priority_counts?.high || 0}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <SecurityIcon color="error" sx={{ mr: 2 }} />
                    <Box>
                      <Typography color="textSecondary" gutterBottom>
                        Critical
                      </Typography>
                      <Typography variant="h4">
                        {reportsStats?.priority_counts?.critical || 0}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Reports Filters */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Filters</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={reportsFilter.status || ''}
                    onChange={(e) => setReportsFilter({...reportsFilter, status: e.target.value})}
                    label="Status"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="reviewed">Reviewed</MenuItem>
                    <MenuItem value="resolved">Resolved</MenuItem>
                    <MenuItem value="dismissed">Dismissed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={reportsFilter.report_type || ''}
                    onChange={(e) => setReportsFilter({...reportsFilter, report_type: e.target.value})}
                    label="Type"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="fake_webshop">Fake Webshop</MenuItem>
                    <MenuItem value="phishing">Phishing</MenuItem>
                    <MenuItem value="malware">Malware</MenuItem>
                    <MenuItem value="scam">Scam</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={reportsFilter.priority || ''}
                    onChange={(e) => setReportsFilter({...reportsFilter, priority: e.target.value})}
                    label="Priority"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="critical">Critical</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {/* Reports Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Reporter</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportsLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : reports.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No reports found
                    </TableCell>
                  </TableRow>
                ) : (
                  reports.map((report) => (
                    <TableRow key={report.id}>
                      <TableCell>{report.id}</TableCell>
                      <TableCell>
                        <Chip 
                          label={report.report_type.replace('_', ' ')} 
                          size="small"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={report.description}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {report.description.length > 50 ? 
                              `${report.description.substring(0, 50)}...` : 
                              report.description
                            }
                          </Typography>
                        </Tooltip>
                        {report.attachments && report.attachments.length > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            📎 {report.attachments.length} file(s)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{report.reporter_email}</TableCell>
                      <TableCell>
                        <Chip 
                          label={report.priority} 
                          size="small"
                          color={
                            report.priority === 'critical' ? 'error' :
                            report.priority === 'high' ? 'warning' :
                            report.priority === 'medium' ? 'info' : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={report.status} 
                          size="small"
                          color={
                            report.status === 'resolved' ? 'success' :
                            report.status === 'dismissed' ? 'error' :
                            report.status === 'reviewed' ? 'info' : 'warning'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(report.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleViewReport(report)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleUpdateReportStatus(report)}
                        >
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box display="flex" justifyContent="center" mt={3}>
            <Button
              variant="outlined"
              onClick={() => setReportsPage(reportsPage - 1)}
              disabled={reportsPage === 0 || reportsLoading}
            >
              Previous
            </Button>
            <Typography sx={{ mx: 2, alignSelf: 'center' }}>
              Page {reportsPage + 1}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setReportsPage(reportsPage + 1)}
              disabled={(reportsPage + 1) * reportsRowsPerPage >= reportsTotal || reportsLoading}
            >
              Next
            </Button>
          </Box>
        </TabPanel>

        {/* Questions Management Tab */}
        <TabPanel value={tabValue} index={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5">Questions Management</Typography>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={() => {
                fetchQuestions();
                fetchPendingQuestionsCount();
                fetchAllCategories();
              }}
            >
              Refresh
            </Button>
          </Box>

          {/* Questions Filters */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Filters</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={questionsFilter.status || ''}
                    onChange={(e) => {
                      setQuestionsFilter({...questionsFilter, status: e.target.value});
                      setQuestionsPage(0);
                    }}
                    label="Status"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="answered">Answered</MenuItem>
                    <MenuItem value="dismissed">Dismissed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {/* Questions Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Question</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Asked By</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {questionsLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : questions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No questions found
                    </TableCell>
                  </TableRow>
                ) : (
                  questions.map((question) => (
                    <TableRow key={question.id}>
                      <TableCell>{question.id}</TableCell>
                      <TableCell>
                        <Tooltip title={question.question}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                            {question.question.length > 80 ? 
                              `${question.question.substring(0, 80)}...` : 
                              question.question
                            }
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {question.kategorie ? (
                          <Chip 
                            label={question.kategorie} 
                            size="small"
                            color="primary"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>{question.is_anonymous ? 'Anonym' : (question.who_asked || question.user_email || 'Unknown')}</TableCell>
                      <TableCell>
                        {question.city && question.country ? (
                          <Typography variant="body2">{question.city}, {question.country}</Typography>
                        ) : question.country ? (
                          <Typography variant="body2">{question.country}</Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={question.status} 
                          size="small"
                          color={
                            question.status === 'answered' ? 'success' :
                            question.status === 'dismissed' ? 'error' : 'warning'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(question.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleViewQuestion(question)}
                          title="View Question"
                        >
                          <VisibilityIcon />
                        </IconButton>
                        {question.status === 'pending' && (
                          <IconButton
                            size="small"
                            onClick={() => handleAnswerQuestion(question)}
                            title="Answer Question"
                            color="primary"
                          >
                            <QuestionAnswerIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box display="flex" justifyContent="center" mt={3}>
            <Button
              variant="outlined"
              onClick={() => setQuestionsPage(questionsPage - 1)}
              disabled={questionsPage === 0 || questionsLoading}
            >
              Previous
            </Button>
            <Typography sx={{ mx: 2, alignSelf: 'center' }}>
              Page {questionsPage + 1} of {Math.ceil(questionsTotal / questionsRowsPerPage)}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setQuestionsPage(questionsPage + 1)}
              disabled={(questionsPage + 1) * questionsRowsPerPage >= questionsTotal || questionsLoading}
            >
              Next
            </Button>
          </Box>
        </TabPanel>

        {/* Calendar Management Tab */}
        <TabPanel value={tabValue} index={5}>
          <Box sx={{ mt: -3 }}>
            <SecEventCalendar />
          </Box>
        </TabPanel>

        {/* Event Suggestions Tab */}
        <TabPanel value={tabValue} index={6}>
          <Box sx={{ mt: -3 }}>
            <EventSuggestionsManagement />
          </Box>
        </TabPanel>

        {/* Video Analytics Tab */}
        <TabPanel value={tabValue} index={7}>
          <Typography variant="h5" gutterBottom>
            Video Analytics
          </Typography>
          
          {/* Stat Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Video Views
                  </Typography>
                  <Typography variant="h4">
                    {videoStats.total_views.toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Unique Viewers
                  </Typography>
                  <Typography variant="h4">
                    {videoStats.unique_viewers.toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Average Rating
                  </Typography>
                  <Typography variant="h4">
                    {videoStats.average_rating ? `${videoStats.average_rating.toFixed(1)} ⭐` : 'N/A'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Feedback
                  </Typography>
                  <Typography variant="h4">
                    {videoStats.total_feedback.toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Video Statistics Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel>Video Titel</TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel>Module</TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel>Item ID</TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel>Video Type</TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel>Views</TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel>Unique Viewers</TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel>Feedback Count</TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel>Average Rating</TableSortLabel>
                  </TableCell>
                  <TableCell>Feedbacks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {videoStats.videos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No video statistics available
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  videoStats.videos.map((video, index) => {
                    const videoTitle = getVideoTitle(video.module_name, video.item_id, video.video_type);
                    return (
                      <TableRow key={`${video.module_name}-${video.item_id}-${video.video_type}-${index}`}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {videoTitle || `${video.module_name} (ID: ${video.item_id})`}
                          </Typography>
                          {video.milieu_codes && video.milieu_codes.length > 0 && (
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                              Milieus: {video.milieu_codes.map(code => getMilieuName(code)).join(', ')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{video.module_name}</TableCell>
                        <TableCell>{video.item_id}</TableCell>
                        <TableCell>
                          <Chip
                            label={video.video_type === 'microlearning' ? 'Erklärvideo' : 'Standard-Video'}
                            size="small"
                            color={video.video_type === 'microlearning' ? 'primary' : 'default'}
                          />
                        </TableCell>
                      <TableCell align="right">{video.views.toLocaleString()}</TableCell>
                      <TableCell align="right">{video.unique_viewers.toLocaleString()}</TableCell>
                      <TableCell align="right">{video.feedback_count.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        {video.average_rating ? `${video.average_rating.toFixed(1)} ⭐` : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {video.feedbacks && video.feedbacks.length > 0 ? (
                          <Box>
                            {video.feedbacks.map((feedback, fbIndex) => (
                              <Box key={feedback.id || fbIndex} sx={{ mb: 1, p: 1, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                  <StarIcon sx={{ fontSize: 16, color: '#ffc107' }} />
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {feedback.rating}/4
                                  </Typography>
                                  {feedback.milieu_code && (
                                    <Chip
                                      label={getMilieuName(feedback.milieu_code)}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.7rem',
                                        backgroundColor: 'rgba(66, 165, 245, 0.2)',
                                        color: '#42a5f5',
                                      }}
                                    />
                                  )}
                                </Box>
                                {feedback.comment && (
                                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontStyle: 'italic', mb: 0.5 }}>
                                    "{feedback.comment}"
                                  </Typography>
                                )}
                                {feedback.created_at && (
                                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem' }}>
                                    {new Date(feedback.created_at).toLocaleDateString('de-DE', { 
                                      year: 'numeric', 
                                      month: '2-digit', 
                                      day: '2-digit',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            Keine Feedbacks
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

      </Paper>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => {
        setEditDialogOpen(false);
        setEditUserData({
          first_name: '',
          last_name: '',
          email: '',
          is_superuser: false,
          is_ambassador: false
        });
      }} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="First Name"
                value={editUserData.first_name}
                onChange={(e) => setEditUserData({
                  ...editUserData,
                  first_name: e.target.value
                })}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Last Name"
                value={editUserData.last_name}
                onChange={(e) => setEditUserData({
                  ...editUserData,
                  last_name: e.target.value
                })}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Email"
                value={editUserData.email}
                onChange={(e) => setEditUserData({
                  ...editUserData,
                  email: e.target.value
                })}
                margin="normal"
              />
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editUserData.is_superuser}
                      onChange={(e) => setEditUserData({
                        ...editUserData,
                        is_superuser: e.target.checked
                      })}
                      disabled={selectedUser.id === currentUser?.id}
                    />
                  }
                  label="Admin User"
                />
                {selectedUser.id === currentUser?.id && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>
                    Cannot remove your own admin privileges
                  </Typography>
                )}
              </Box>
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editUserData.is_ambassador}
                      onChange={(e) => setEditUserData({
                        ...editUserData,
                        is_ambassador: e.target.checked
                      })}
                      disabled={selectedUser.id === currentUser?.id}
                    />
                  }
                  label="Ambassador"
                />
                {selectedUser.id === currentUser?.id && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>
                    Cannot assign or remove ambassador role for yourself
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialogOpen(false);
            setEditUserData({
              first_name: '',
              last_name: '',
              email: '',
              is_superuser: false,
              is_ambassador: false
            });
          }}>Cancel</Button>
          <Button
            onClick={async () => {
              if (selectedUser) {
                // Prevent admin from removing their own admin privileges
                if (selectedUser.id === currentUser?.id) {
                  if (!editUserData.is_superuser) {
                    showSnackbar('Cannot remove your own admin privileges', 'error');
                    return;
                  }
                  // Prevent self-assignment of ambassador role
                  if (editUserData.is_ambassador !== selectedUser.is_ambassador) {
                    showSnackbar('Cannot assign or remove ambassador role for yourself', 'error');
                    return;
                  }
                }
                
                const updates = {
                  first_name: editUserData.first_name,
                  last_name: editUserData.last_name,
                  email: editUserData.email,
                  is_superuser: editUserData.is_superuser,
                  is_ambassador: editUserData.is_ambassador
                };
                const success = await updateUserMultiple(selectedUser.id, updates);
                if (success) {
                  setEditDialogOpen(false);
                }
              }
            }}
            variant="contained"
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={passwordResetDialogOpen} onClose={() => setPasswordResetDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body1" gutterBottom>
                Reset password for: <strong>{selectedUser.first_name} {selectedUser.last_name}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Email: {selectedUser.email}
              </Typography>
              <TextField
                fullWidth
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                margin="normal"
                helperText="Password must be at least 12 characters long"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setPasswordResetDialogOpen(false);
            setNewPassword('');
            setShowPassword(false);
          }}>
            Cancel
          </Button>
          <Button
            onClick={resetUserPassword}
            variant="contained"
            color="warning"
            disabled={!newPassword || newPassword.length < 12}
          >
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>


      {/* MFA Disable Dialog */}
      <Dialog open={mfaDisableDialogOpen} onClose={() => setMfaDisableDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Disable MFA for {selectedUser?.first_name} {selectedUser?.last_name}</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ pt: 2 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Admin Override:</strong> This will permanently disable MFA for this user without requiring verification. 
                Use this only when the user is locked out and cannot provide their MFA code.
              </Alert>
              <Typography variant="body1" gutterBottom>
                The user will need to set up MFA again if they want to re-enable it.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                <strong>User:</strong> {selectedUser.first_name} {selectedUser.last_name}<br/>
                <strong>Email:</strong> {selectedUser.email}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMfaDisableDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={disableMfa}
            variant="contained"
            color="error"
          >
            Disable MFA (Admin Override)
          </Button>
        </DialogActions>
      </Dialog>

      {/* Force Logout Dialog */}
      <Dialog open={forceLogoutDialogOpen} onClose={() => setForceLogoutDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Force Logout {selectedUser?.first_name} {selectedUser?.last_name}</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ pt: 2 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Force Logout:</strong> This will immediately log out the user from all devices and revoke all their active sessions.
                They will need to log in again to access their account.
              </Alert>
              <Typography variant="body1" gutterBottom>
                This action is useful for:
              </Typography>
              <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                <Typography component="li" variant="body2">
                  Security incidents or suspected account compromise
                </Typography>
                <Typography component="li" variant="body2">
                  When a user reports unauthorized access
                </Typography>
                <Typography component="li" variant="body2">
                  Emergency situations requiring immediate session termination
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                <strong>User:</strong> {selectedUser.first_name} {selectedUser.last_name}<br/>
                <strong>Email:</strong> {selectedUser.email}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForceLogoutDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={forceLogoutUser}
            variant="contained"
            color="warning"
          >
            Force Logout
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Report Dialog */}
      <Dialog open={viewReportDialogOpen} onClose={() => setViewReportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Report Details</DialogTitle>
        <DialogContent>
          {selectedReport && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Report ID</Typography>
                  <Typography variant="body1">{selectedReport.id}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Type</Typography>
                  <Chip label={selectedReport.report_type.replace('_', ' ')} color="primary" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Priority</Typography>
                  <Chip 
                    label={selectedReport.priority} 
                    color={
                      selectedReport.priority === 'critical' ? 'error' :
                      selectedReport.priority === 'high' ? 'warning' :
                      selectedReport.priority === 'medium' ? 'info' : 'default'
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  <Chip 
                    label={selectedReport.status} 
                    color={
                      selectedReport.status === 'resolved' ? 'success' :
                      selectedReport.status === 'dismissed' ? 'error' :
                      selectedReport.status === 'reviewed' ? 'info' : 'warning'
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Reporter</Typography>
                  <Typography variant="body1">{selectedReport.reporter_email}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Created</Typography>
                  <Typography variant="body1">{new Date(selectedReport.created_at).toLocaleString()}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Description</Typography>
                  <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>{selectedReport.description}</Typography>
                </Grid>
                {selectedReport.url && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">URL</Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      <a href={selectedReport.url} target="_blank" rel="noopener noreferrer">
                        {selectedReport.url}
                      </a>
                    </Typography>
                  </Grid>
                )}
                {selectedReport.attachments && selectedReport.attachments.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Attachments</Typography>
                    <Box sx={{ mt: 1 }}>
                      {selectedReport.attachments.map((attachment, index) => (
                        <Box key={attachment.id} sx={{ 
                          border: '1px solid #e0e0e0', 
                          borderRadius: 1, 
                          mb: 2,
                          backgroundColor: '#f5f5f5',
                          overflow: 'hidden'
                        }}>
                          {/* File info header */}
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            p: 1, 
                            backgroundColor: '#e8e8e8',
                            borderBottom: '1px solid #e0e0e0'
                          }}>
                            <Typography variant="body2" sx={{ flexGrow: 1 }}>
                              📎 {attachment.filename} ({(attachment.file_size / 1024 / 1024).toFixed(2)} MB)
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {attachment.mime_type}
                            </Typography>
                          </Box>
                          
                          {/* Image preview */}
                          {attachment.mime_type && attachment.mime_type.startsWith('image/') && (
                            <Box sx={{ p: 1 }}>
                              <ImagePreview attachmentId={attachment.id} filename={attachment.filename} />
                            </Box>
                          )}
                          
                          {/* Non-image files */}
                          {attachment.mime_type && !attachment.mime_type.startsWith('image/') && (
                            <Box sx={{ p: 2, textAlign: 'center' }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                File type: {attachment.mime_type}
                              </Typography>
                              <Button 
                                variant="outlined" 
                                size="small"
                                onClick={() => window.open(`/api/v1/reports/attachments/${attachment.id}`, '_blank')}
                              >
                                Download File
                              </Button>
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewReportDialogOpen(false)}>Close</Button>
          <Button 
            onClick={() => {
              setViewReportDialogOpen(false);
              handleUpdateReportStatus(selectedReport);
            }}
            variant="contained"
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Report Status Dialog */}
      <Dialog open={updateStatusDialogOpen} onClose={() => setUpdateStatusDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Report Status</DialogTitle>
        <DialogContent>
          {selectedReport && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body1" gutterBottom>
                Report ID: {selectedReport.id}
              </Typography>
              <FormControl fullWidth margin="normal">
                <InputLabel>New Status</InputLabel>
                <Select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  label="New Status"
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="reviewed">Reviewed</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                  <MenuItem value="dismissed">Dismissed</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes (optional)"
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
                margin="normal"
                helperText="Add any notes about the status change"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateStatusDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={updateReportStatus}
            variant="contained"
            disabled={!newStatus}
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Question Dialog */}
      <Dialog open={viewQuestionDialogOpen} onClose={() => setViewQuestionDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Question Details</DialogTitle>
        <DialogContent>
          {selectedQuestion && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Question ID</Typography>
                  <Typography variant="body1">{selectedQuestion.id}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  <Chip 
                    label={selectedQuestion.status} 
                    color={
                      selectedQuestion.status === 'answered' ? 'success' :
                      selectedQuestion.status === 'dismissed' ? 'error' : 'warning'
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Asked By</Typography>
                  <Typography variant="body1">
                    {selectedQuestion.is_anonymous ? 'Anonym' : (selectedQuestion.who_asked || selectedQuestion.user_email || 'Unknown')}
                  </Typography>
                </Grid>
                {selectedQuestion.kategorie && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Category</Typography>
                    <Chip label={selectedQuestion.kategorie} color="primary" />
                  </Grid>
                )}
                {selectedQuestion.city && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">City</Typography>
                    <Typography variant="body1">{selectedQuestion.city}</Typography>
                  </Grid>
                )}
                {selectedQuestion.country && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Country</Typography>
                    <Typography variant="body1">{selectedQuestion.country}</Typography>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Created</Typography>
                  <Typography variant="body1">{new Date(selectedQuestion.created_at).toLocaleString()}</Typography>
                </Grid>
                {selectedQuestion.answered_at && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Answered At</Typography>
                    <Typography variant="body1">{new Date(selectedQuestion.answered_at).toLocaleString()}</Typography>
                  </Grid>
                )}
                {selectedQuestion.expert_email && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Answered By</Typography>
                    <Typography variant="body1">{selectedQuestion.expert_email}</Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>Question</Typography>
                  <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap', p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    {selectedQuestion.question}
                  </Typography>
                </Grid>
                {selectedQuestion.answer && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>Answer</Typography>
                    <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap', p: 2, bgcolor: '#e8f5e9', borderRadius: 1 }}>
                      {selectedQuestion.answer}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewQuestionDialogOpen(false)}>Close</Button>
          {selectedQuestion && selectedQuestion.status === 'pending' && (
            <Button 
              onClick={() => {
                setViewQuestionDialogOpen(false);
                handleAnswerQuestion(selectedQuestion);
              }}
              variant="contained"
              startIcon={<QuestionAnswerIcon />}
            >
              Answer Question
            </Button>
          )}
          {selectedQuestion && selectedQuestion.status !== 'dismissed' && (
            <Button 
              onClick={() => {
                setQuestionToDismiss(selectedQuestion);
                setDismissReasonDialogOpen(true);
              }}
              variant="outlined"
              color="error"
            >
              Dismiss
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Answer Question Dialog */}
      <Dialog open={answerQuestionDialogOpen} onClose={() => setAnswerQuestionDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Answer Question</DialogTitle>
        <DialogContent>
          {selectedQuestion && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>Question ID</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{selectedQuestion.id}</Typography>
              
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>Question</Typography>
              <Typography variant="body1" sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, whiteSpace: 'pre-wrap' }}>
                {selectedQuestion.question}
              </Typography>
              
              <Autocomplete
                freeSolo
                options={availableCategories}
                value={questionCategory || null}
                onChange={(event, newValue) => {
                  setQuestionCategory(newValue || '');
                }}
                onInputChange={(event, newInputValue) => {
                  setQuestionCategory(newInputValue);
                }}
                renderInput={(params) => (
                  <MuiTextField
                    {...params}
                    label="Category (optional)"
                    margin="normal"
                    helperText="Select an existing category or type a new one"
                    fullWidth
                  />
                )}
                sx={{ mt: 2 }}
              />
              
              <TextField
                fullWidth
                multiline
                rows={8}
                label="Your Answer *"
                value={questionAnswer}
                onChange={(e) => setQuestionAnswer(e.target.value)}
                margin="normal"
                helperText={`Max. 2000 characters - ${questionAnswer.length}/2000`}
                inputProps={{ maxLength: 2000 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAnswerQuestionDialogOpen(false);
            setQuestionAnswer('');
            setQuestionCategory('');
          }}>Cancel</Button>
          <Button 
            onClick={submitQuestionAnswer}
            variant="contained"
            disabled={!questionAnswer.trim()}
          >
            Submit Answer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dismiss Reason Dialog */}
      <Dialog open={dismissReasonDialogOpen} onClose={() => {
        setDismissReasonDialogOpen(false);
        setDismissReason('');
        setQuestionToDismiss(null);
      }} maxWidth="md" fullWidth>
        <DialogTitle>Dismiss Question</DialogTitle>
        <DialogContent>
          {questionToDismiss && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>Question ID</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{questionToDismiss.id}</Typography>
              
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>Question</Typography>
              <Typography variant="body1" sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, whiteSpace: 'pre-wrap' }}>
                {questionToDismiss.question}
              </Typography>
              
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Reason for Dismissal *"
                value={dismissReason}
                onChange={(e) => setDismissReason(e.target.value)}
                margin="normal"
                helperText={`Please provide a reason for dismissing this question (required) - ${dismissReason.length}/1000`}
                inputProps={{ maxLength: 1000 }}
                required
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDismissReasonDialogOpen(false);
            setDismissReason('');
            setQuestionToDismiss(null);
          }}>Cancel</Button>
          <Button 
            onClick={async () => {
              if (!dismissReason.trim()) {
                setSnackbar({
                  open: true,
                  message: 'Please provide a reason for dismissing',
                  severity: 'error'
                });
                return;
              }
              
              await updateQuestionStatus(questionToDismiss.id, 'dismissed', dismissReason.trim());
              setViewQuestionDialogOpen(false);
              setDismissReasonDialogOpen(false);
              setDismissReason('');
              setQuestionToDismiss(null);
            }}
            variant="contained"
            color="error"
            disabled={!dismissReason.trim()}
          >
            Dismiss Question
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminDashboard;
